import React, { useState, useEffect } from "react";
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  Button,
  Divider,
  Chip,
  Tooltip,
  Alert,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  InboxOutlined as InboxIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  MarkEmailRead as MarkReadIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { notificationService } from "services/notification.service";

const NotificationBell = ({ variant = "default" }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationFilter, setNotificationFilter] = useState("all");
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch unread count
  const fetchUnreadCount = async () => {
    try {
      const result = await notificationService.getUnreadCount();
      setUnreadCount(result.unreadCount);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  // Fetch notifications
  const fetchNotifications = async (filter = notificationFilter) => {
    try {
      setLoading(true);
      const filters =
        filter === "all"
          ? {}
          : { isRead: filter === "read" ? "true" : "false" };
      const result = await notificationService.getNotifications(
        1,
        10,
        filters
      ); // Lấy 10 thông báo gần nhất
      setNotifications(result.notifications);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      showAlert("Có lỗi khi tải thông báo", "error");
    } finally {
      setLoading(false);
    }
  };

  // Show alert
  const showAlert = (message, severity = "success") => {
    setAlert({ open: true, message, severity });
    setTimeout(
      () => setAlert({ open: false, message: "", severity: "success" }),
      3000
    );
  };

  // Load data on component mount
  useEffect(() => {
    fetchUnreadCount();

    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);

    return () => clearInterval(interval);
  }, []);

  // Handle popover open
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications(notificationFilter);
  };

  const handleFilterChange = (filter) => {
    if (filter === notificationFilter) return;

    setNotificationFilter(filter);
    fetchNotifications(filter);
  };

  // Handle popover close
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Mark notification as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications((prev) =>
        notificationFilter === "unread"
          ? prev.filter((notif) => notif.notificationId !== notificationId)
          : prev.map((notif) =>
              notif.notificationId === notificationId
                ? { ...notif, isRead: true }
                : notif
            )
      );
      fetchUnreadCount(); // Refresh unread count
      showAlert("Đã đánh dấu thông báo đã đọc");
    } catch (error) {
      showAlert("Có lỗi khi đánh dấu thông báo", "error");
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications((prev) =>
        notificationFilter === "unread"
          ? []
          : prev.map((notif) => ({ ...notif, isRead: true }))
      );
      setUnreadCount(0);
      showAlert("Đã đánh dấu tất cả thông báo đã đọc");
    } catch (error) {
      showAlert("Có lỗi khi đánh dấu tất cả thông báo", "error");
    }
  };

  // Delete notification
  const handleDelete = async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);
      setNotifications((prev) =>
        prev.filter((notif) => notif.notificationId !== notificationId)
      );
      fetchUnreadCount(); // Refresh unread count
      showAlert("Đã xóa thông báo");
    } catch (error) {
      showAlert("Có lỗi khi xóa thông báo", "error");
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 4:
        return "error"; // Critical
      case 3:
        return "warning"; // High
      case 2:
        return "primary"; // Normal
      case 1:
        return "default"; // Low
      default:
        return "default";
    }
  };

  // Get priority label
  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 4:
        return "Khẩn cấp";
      case 3:
        return "Cao";
      case 2:
        return "Bình thường";
      case 1:
        return "Thấp";
      default:
        return "Bình thường";
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return "Vừa xong";
    if (diffInMinutes < 60) return `${diffInMinutes} phút trước`;
    if (diffInMinutes < 1440)
      return `${Math.floor(diffInMinutes / 60)} giờ trước`;

    return date.toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const open = Boolean(anchorEl);
  const BellIcon = variant === "inbox" ? InboxIcon : NotificationsIcon;

  return (
    <>
      {alert.open && (
        <Alert
          severity={alert.severity}
          sx={{
            position: "fixed",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            minWidth: "300px",
          }}
        >
          {alert.message}
        </Alert>
      )}

      <Tooltip title={variant === "inbox" ? "Hộp thư thông báo" : "Thông báo"}>
        <IconButton
          className={variant === "inbox" ? "subcontract-header-action" : ""}
          color="inherit"
          onClick={handleClick}
          sx={variant === "inbox" ? undefined : { mr: 1 }}
        >
          <Badge badgeContent={unreadCount} color="error">
            <BellIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            width: 440,
            maxWidth: "calc(100vw - 24px)",
            maxHeight: 620,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            boxShadow: "0 16px 48px rgba(15, 23, 42, 0.18)",
            overflow: "hidden",
          },
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              pt: 2,
              pb: 1,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: "16px" }}>
              Thông báo ({unreadCount > 0 ? `${unreadCount} mới` : "0"})
            </Typography>
            <Box>
              {unreadCount > 0 && (
                <Tooltip title="Đánh dấu tất cả đã đọc">
                  <IconButton size="small" onClick={handleMarkAllAsRead}>
                    <MarkReadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton size="small" onClick={handleClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Box
            role="tablist"
            aria-label="Lọc thông báo theo trạng thái đọc"
            sx={{ display: "flex", gap: 1, px: 2, pb: 1 }}
          >
            {[
              { value: "all", label: "Tất cả" },
              { value: "unread", label: "Chưa đọc" },
              { value: "read", label: "Đã đọc" },
            ].map((filter) => {
              const selected = notificationFilter === filter.value;

              return (
                <Button
                  key={filter.value}
                  role="tab"
                  aria-selected={selected}
                  size="small"
                  variant={selected ? "contained" : "outlined"}
                  onClick={() => handleFilterChange(filter.value)}
                  sx={{
                    minWidth: 0,
                    px: 1.25,
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 600,
                    boxShadow: "none",
                  }}
                >
                  {filter.label}
                </Button>
              );
            })}
          </Box>

          <Divider />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2.5,
              pt: 1,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {notificationFilter === "unread"
                ? "Chưa đọc"
                : notificationFilter === "read"
                ? "Đã đọc"
                : "Gần đây"}
            </Typography>
            <Button
              size="small"
              onClick={() => {
                handleClose();
                navigate("/notifications");
              }}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Xem tất cả ›
            </Button>
          </Box>

          {loading ? (
            <Typography sx={{ px: 2.5, py: 4, textAlign: "center" }}>
              Đang tải...
            </Typography>
          ) : notifications.length === 0 ? (
            <Typography
              sx={{ px: 2.5, py: 4, textAlign: "center", color: "text.secondary" }}
            >
              {notificationFilter === "unread"
                ? "Không có thông báo chưa đọc"
                : notificationFilter === "read"
                ? "Không có thông báo đã đọc"
                : "Không có thông báo nào"}
            </Typography>
          ) : (
            <List sx={{ maxHeight: 390, overflow: "auto", px: 2.5, py: 0 }}>
              {notifications.map((notification) => (
                <ListItem
                  key={notification.notificationId}
                  sx={{
                    position: "relative",
                    backgroundColor: "transparent",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&:last-of-type": { borderBottom: 0 },
                    "&:hover": { backgroundColor: "action.hover" },
                    flexDirection: "column",
                    alignItems: "flex-start",
                    px: 0,
                    py: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    {!notification.isRead && (
                      <Box
                        aria-label="Chưa đọc"
                        sx={{
                          width: 9,
                          height: 9,
                          mt: 0.75,
                          mr: 1.1,
                          flexShrink: 0,
                          borderRadius: "50%",
                          bgcolor: "primary.main",
                        }}
                      />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            minWidth: 0,
                            fontWeight: notification.isRead ? 500 : 700,
                            fontSize: "14px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {notification.title}
                        </Typography>
                        <Chip
                          label={getPriorityLabel(notification.priority)}
                          color={getPriorityColor(notification.priority)}
                          size="small"
                          sx={{ flexShrink: 0, fontSize: "11px", height: 20 }}
                        />
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontSize: "12px",
                          lineHeight: 1.4,
                          mb: 0.75,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {notification.message}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ fontSize: "11px" }}
                      >
                        {formatDate(notification.createdAt)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", gap: 0.25, flexShrink: 0 }}>
                      {!notification.isRead && (
                        <Tooltip title="Đánh dấu đã đọc">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleMarkAsRead(notification.notificationId)
                            }
                            sx={{ p: 0.5 }}
                          >
                            <MarkReadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title="Xóa">
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleDelete(notification.notificationId)
                          }
                          sx={{ p: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}

        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
